import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { voiceApi } from '../../api/voice.api.js';
import { useTwilioDevice } from '../../hooks/useTwilioDevice.js';
import { useUiStore } from '../../store/uiStore.js';
import { IconMic, IconPhone, IconTrash, IconX } from '../icons/Icons.jsx';

function duration(seconds) {
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

const RECENTS_KEY = 'bulksms.voice.recentNumbers';

function cleanDialInput(value) {
  return String(value || '')
    .replace(/[^\d+#*]/g, '')
    .replace(/(?!^)\+/g, '')
    .slice(0, 24);
}

function loadRecents() {
  try {
    const saved = JSON.parse(localStorage.getItem(RECENTS_KEY) || '[]');
    return Array.isArray(saved) ? saved.slice(0, 6) : [];
  } catch {
    return [];
  }
}

export function Softphone({ enabled }) {
  const {
    status, lastError, incoming, outgoing, activeCall,
    accept, reject, makeCall, cancelOutgoing, hangup, mute, register,
  } = useTwilioDevice(enabled);
  const open = useUiStore((s) => s.phoneOpen);
  const setOpen = useUiStore((s) => s.setPhoneOpen);
  const setPhoneState = useUiStore((s) => s.setPhoneState);
  const [dialTo, setDialTo] = useState('');
  const [muted, setMuted] = useState(false);
  const [transferTo, setTransferTo] = useState('');
  const [seconds, setSeconds] = useState(0);
  const [dismissedError, setDismissedError] = useState('');
  const [recents, setRecents] = useState(loadRecents);

  useEffect(() => {
    if (incoming || outgoing || activeCall) setOpen(true);
  }, [incoming, outgoing, activeCall]);

  useEffect(() => {
    const onStartCall = (event) => {
      const destination = cleanDialInput(event.detail?.phone || event.detail?.to || '');
      if (!destination) return;
      setDialTo(destination);
      setOpen(true);
    };
    window.addEventListener('bulk-sms-crm:start-call', onStartCall);
    return () => window.removeEventListener('bulk-sms-crm:start-call', onStartCall);
  }, []);

  useEffect(() => {
    setDismissedError('');
  }, [lastError]);

  useEffect(() => {
    if (!activeCall) return setSeconds(0);
    const timer = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(timer);
  }, [activeCall]);

  // The top bar phone button mirrors call state, since the panel no longer has its own launcher.
  useEffect(() => {
    setPhoneState({
      status,
      live: Boolean(activeCall || incoming || outgoing),
      label: activeCall ? duration(seconds) : incoming ? 'Incoming' : outgoing ? 'Calling' : '',
      hasError: Boolean(lastError),
    });
  }, [status, activeCall, incoming, outgoing, seconds, lastError, setPhoneState]);

  if (!enabled) return null;

  const dial = async () => {
    try {
      const destination = cleanDialInput(dialTo);
      await makeCall(destination);
      const next = [destination, ...recents.filter(n => n !== destination)].slice(0, 6);
      setRecents(next);
      localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
      setOpen(false);
    } catch (err) {
      toast.error(err.message || 'Unable to start call');
    }
  };

  const enablePhone = async () => {
    const ok = await register({ requestAudio: true });
    if (ok) toast.success('Phone is ready on this device');
  };

  const transfer = async () => {
    const callSid = activeCall?.parameters?.CallSid;
    if (!callSid || !transferTo.trim()) return toast.error('Enter a transfer number');
    await voiceApi.transfer({ callSid, to: transferTo.trim() });
    toast.success('Call transferred');
    hangup();
  };

  const closePanel = () => {
    setOpen(false);
    if (lastError) setDismissedError(lastError);
  };

  const visibleError = lastError && dismissedError !== lastError;
  const connectionTone = status === 'Connected' ? 'var(--success)' : status === 'Connecting' ? 'var(--warning)' : 'var(--danger)';

  const panelStyle = {
    background: 'var(--card)',
    border: '1px solid var(--borderHi)',
    boxShadow: 'var(--shadowFloat)',
  };

  return (
    <>
      {open && (
        <div
          className="fixed z-50 inset-x-3 top-[72px] max-h-[calc(100dvh-10rem)] overflow-y-auto rounded-2xl p-3.5 space-y-3 sm:inset-x-auto sm:right-4 sm:w-[min(92vw,360px)] sm:max-h-[min(620px,calc(100dvh-6rem))] sm:p-4"
          style={panelStyle}
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-bold" style={{ color: 'var(--text)' }}>Voice Calls</p>
              <p className="text-xs" style={{ color: connectionTone }}>{status}</p>
            </div>
            <button type="button" onClick={closePanel} className="tap-target rounded-xl flex items-center justify-center" style={{ color: 'var(--textMuted)' }} aria-label="Close phone">
              <IconX size={17} />
            </button>
          </div>

          {incoming && (
            <div className="rounded-xl p-3 space-y-3" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
              <p className="text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--accent)' }}>Incoming call</p>
              <p className="text-sm font-bold" style={{ color: 'var(--text)' }}>{incoming.parameters?.From || 'Unknown caller'}</p>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => { accept(); setMuted(false); }} className="btn-primary text-sm"><IconPhone size={15} /> Accept</button>
                <button type="button" onClick={reject} className="btn-secondary text-sm"><IconX size={15} /> Reject</button>
              </div>
            </div>
          )}

          {outgoing && (
            <div className="rounded-xl p-3 text-center" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
              <p className="text-sm font-bold" style={{ color: 'var(--text)' }}>Calling...</p>
              <p className="text-xs mt-1" style={{ color: 'var(--textMuted)' }}>{dialTo}</p>
              <button type="button" onClick={cancelOutgoing} className="btn-secondary mt-3 w-full text-sm"><IconX size={15} /> Cancel</button>
            </div>
          )}

          {activeCall && (
            <div className="rounded-xl p-3 space-y-3" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
              <div>
                <p className="text-sm font-bold" style={{ color: 'var(--text)' }}>Active call</p>
                <p className="text-xs" style={{ color: 'var(--textMuted)' }}>{activeCall.parameters?.From || activeCall.parameters?.To || 'Voice call'} · {duration(seconds)}</p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => { mute(!muted); setMuted(!muted); }} className="btn-secondary text-sm"><IconMic size={15} /> {muted ? 'Unmute' : 'Mute'}</button>
                <button type="button" onClick={() => { hangup(); setMuted(false); }} className="btn-primary text-sm"><IconX size={15} /> Hang up</button>
              </div>
              <div className="grid sm:grid-cols-[1fr_auto] gap-2">
                <input className="input text-sm" value={transferTo} onChange={e => setTransferTo(cleanDialInput(e.target.value))} placeholder="Transfer to..." />
                <button type="button" onClick={transfer} className="btn-secondary text-sm w-full sm:w-auto">Transfer</button>
              </div>
            </div>
          )}

          {!incoming && !outgoing && !activeCall && (
            <div className="space-y-3">
              <input className="input text-lg" type="tel" value={dialTo} onChange={e => setDialTo(cleanDialInput(e.target.value))} onKeyDown={e => e.key === 'Enter' && dial()} placeholder="+15551234567" />
              {recents.length > 0 && (
                <div className="flex gap-1.5 overflow-x-auto no-scrollbar">
                  {recents.map(number => (
                    <button
                      key={number}
                      type="button"
                      onClick={() => setDialTo(number)}
                      className="flex-shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold"
                      style={{ background: 'var(--surface)', color: 'var(--textMuted)', border: '1px solid var(--border)' }}
                    >
                      {number}
                    </button>
                  ))}
                </div>
              )}
              <div className="grid grid-cols-3 gap-2">
                {['1','2','3','4','5','6','7','8','9','*','0','#'].map(digit => (
                  <button key={digit} type="button" className="btn-secondary h-10" onClick={() => setDialTo(v => `${v}${digit}`)}>{digit}</button>
                ))}
              </div>
              <div className="grid grid-cols-[1fr_auto] gap-2">
                <button type="button" className="btn-secondary h-10" onClick={() => setDialTo(v => `${v}+`)}>+ Country Code</button>
                <button type="button" className="btn-secondary h-10 px-4" onClick={() => setDialTo(v => v.slice(0, -1))}><IconTrash size={15} /></button>
              </div>
              <button type="button" onClick={dial} disabled={!dialTo.trim()} className="btn-primary w-full">
                <IconPhone size={16} /> {status === 'Connected' ? 'Call' : 'Enable & Call'}
              </button>
              {status !== 'Connected' && !visibleError && (
                <div className="rounded-xl p-3 text-xs flex items-center justify-between gap-3" style={{ background: 'var(--surface)', color: 'var(--textMuted)', border: '1px solid var(--border)' }}>
                  <span>Voice device is {status.toLowerCase()}.</span>
                  <button type="button" onClick={enablePhone} className="font-bold" style={{ color: 'var(--accent)' }}>Enable</button>
                </div>
              )}
              {visibleError && (
                <div className="rounded-xl p-3 text-xs" style={{ background: 'var(--warningSoft)', color: 'var(--warningSoftText)' }}>
                  <div className="flex items-start justify-between gap-3">
                    <span>{lastError}</span>
                    <button type="button" onClick={() => setDismissedError(lastError)} className="font-bold">Hide</button>
                  </div>
                  <p className="mt-2 leading-relaxed" style={{ color: 'var(--textMuted)' }}>
                    On mobile, allow microphone permission and use a stable data connection. If Retry still fails, confirm the customer's TwiML App Voice URL is saved in Twilio with HTTP POST.
                  </p>
                  <button type="button" onClick={enablePhone} className="block mt-2 underline">Enable phone on this device</button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}
